<script setup>

import {computed, onMounted, ref, watch} from "vue";

const reach = ref(1000);
const conversionRateToStep2 = ref(10);
const conversionRateToPurchase = ref(20);
const pricePerMonth = ref(50);
const monthsStayed = ref(12);

// Calculate initial churn from months stayed: monthly churn % = (1 / monthsStayed) * 100
// Default to 5% (positive default = 20 months retention)
const calculateChurnFromMonthsStayed = (months) => {
    if (months > 0) {
        return (1 / months) * 100;
    }
    return 5; // Positive default: 5% monthly churn
};

const monthlyChurnRate = ref(calculateChurnFromMonthsStayed(monthsStayed.value));

const transitioned = ref(false)
const calculating = ref(false);
const seeResult = ref(false);

// Update churn when months stayed changes (before results are shown)
watch(() => monthsStayed.value, (newValue) => {
    if (!seeResult.value) {
        monthlyChurnRate.value = calculateChurnFromMonthsStayed(newValue);
    }
});

const results = computed(() => {
    const step2Leads = reach.value * (conversionRateToStep2.value / 100);
    const purchases = step2Leads * (conversionRateToPurchase.value / 100);
    const revenuePerMonth = purchases * pricePerMonth.value;
    const totalRevenuePerYear = revenuePerMonth * 12;

    // Required new members per month to maintain constant revenue accounting for churn
    // Monthly churn rate is adjustable (as percentage)
    // Required new members = purchases * (monthlyChurnRate / 100)
    const requiredNewMembersPerMonth = purchases * (monthlyChurnRate.value / 100);

    return {
        step2Leads: step2Leads.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        step2LeadsRaw: step2Leads,
        purchases: purchases.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        purchasesRaw: purchases,
        revenuePerMonth: revenuePerMonth.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        revenuePerMonthRaw: revenuePerMonth,
        totalRevenuePerYear: totalRevenuePerYear.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        totalRevenuePerYearRaw: totalRevenuePerYear,
        requiredNewMembersPerMonth: requiredNewMembersPerMonth.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        requiredNewMembersPerMonthRaw: requiredNewMembersPerMonth,
        monthlyChurnRate: monthlyChurnRate.value.toFixed(2),
    };
})

const calculateInitial = () => {
    // Set churn from months stayed when calculating
    monthlyChurnRate.value = calculateChurnFromMonthsStayed(monthsStayed.value);

    calculating.value = true

    setTimeout(function(){
        seeResult.value = true;

        setTimeout(function() {
            window.scrollBy({
                top: 500,
                behavior: 'smooth'
            });
        }, 300);
    }, 300)
}

onMounted(() => {
    setTimeout(() => {
        transitioned.value = true;
    }, 100);
})

</script>

<template>
    <div>
        <card-basic
            class="transition-all duration-500"
            :class="transitioned ? 'translate-y-0 scale-100' : '-translate-y-4 opacity-0 scale-95'"
        >
            <div class="grid-cols-2 gap-8 md:grid">
                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Total number of people reached
                    </p>
                    <input-number
                        max="10000000"
                        min="0"
                        :step="100"
                        class="mx-auto w-36"
                        v-model:content="reach"
                        :show-step-ui="true"
                        placeholder="Reach"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Conversion rate to step 2 (meeting or CTA interest)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="conversionRateToStep2"
                            min="0"
                            max="100"
                            :step="1"
                            class="w-full"
                            @update:content="conversionRateToStep2 = $event"
                        />{{ conversionRateToStep2 }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Conversion rate to purchase (percentage of step-2 leads that buy)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="conversionRateToPurchase"
                            min="0"
                            max="100"
                            :step="1"
                            class="w-full"
                            @update:content="conversionRateToPurchase = $event"
                        />{{ conversionRateToPurchase }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Price per month (monthly subscription price)
                    </p>
                    <input-number
                        max="100000"
                        min="0"
                        :step="5"
                        class="mx-auto w-36"
                        v-model:content="pricePerMonth"
                        :show-step-ui="true"
                        placeholder="Price per month"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average retention (months stayed)
                    </p>
                    <input-number
                        max="120"
                        min="1"
                        :step="1"
                        class="mx-auto w-36"
                        v-model:content="monthsStayed"
                        :show-step-ui="true"
                        placeholder="Months stayed"
                    />
                </div>

                <div
                    v-if="! seeResult"
                    class="flex justify-center col-span-2"
                >
                    <button-primary
                        class="justify-center w-48 mx-auto transition-all"
                        :class="calculating ? 'translate-y-8 opacity-0' : ''"
                        @click="calculateInitial"
                    >
                        Calculate
                    </button-primary>
                </div>

                <div
                    v-if="calculating"
                    class="col-span-2 -mx-4 -mt-6 -mb-4 transition-colors duration-1000 text-wlPrimaryContrast md:-mx-6 md:-mb-6 lg:-mx-8 lg:-mb-8"
                    :class="seeResult ? 'bg-wlPrimary' : 'bg-transparent'"
                >
                    <div class="w-full h-12 transform -translate-y-px bg-white rounded-b-wl"></div>
                    <div
                        class="col-span-2 p-4 pt-3 transition-all duration-1000 rounded-b-wl md:p-6 md:pt-4 lg:p-8 lg:pt-5"
                        :class="seeResult ? 'translate-y-0 opacity-100 max-h-screen' : '-translate-y-8 opacity-0 max-h-0'"
                    >
                        <div class="flex mb-8 space-x-4">
                            <div>
                                <i class="mt-1 text-2xl fa-regular fa-chart-line"></i>
                            </div>
                            <div>
                                <p>Out of <strong>{{ reach.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' ') }} people reached</strong>, you would get <strong>{{ results.step2Leads }} step 2 leads</strong> (meeting or CTA interest).</p>

                                <p>Out of these step 2 leads, you would get <strong>{{ results.purchases }} customers</strong>.</p>

                                <p>These customers would generate <strong>${{ results.revenuePerMonth }}</strong> in revenue per month.</p>

                                <p>Total revenue per year: <strong>${{ results.totalRevenuePerYear }}</strong></p>

                                <div class="mt-4 p-4 bg-gray-50 rounded-lg">
                                    <p class="mb-3">
                                        <strong class="text-gray-600">Monthly churn rate: {{ results.monthlyChurnRate }}%</strong>
                                        <span class="text-xs text-gray-500 block mt-1">(Calculated from average retention of {{ monthsStayed }} months)</span>
                                    </p>
                                    <div class="mx-auto md:w-64">
                                        <input-range
                                            :content="monthlyChurnRate"
                                            min="0.1"
                                            max="50"
                                            :step="0.1"
                                            class="w-full"
                                            @update:content="monthlyChurnRate = $event"
                                        />{{ monthlyChurnRate.toFixed(2) }} %
                                    </div>
                                </div>

                                <p class="mt-4">
                                    To maintain constant revenue accounting for churn, you would need <strong>{{ results.requiredNewMembersPerMonth }} new members per month</strong>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card-basic>
    </div>
</template>

<style scoped>

</style>

